import client from '@/http/customer-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { Customer } from '@modules/manifera-user/resources/js/schema.ts'
import { useLocation, useNavigate } from 'react-router-dom'
import i18n from '@/i18n'
import { restApiErrorHandler } from '@/lib/restApiErrorHandler'
import {
  clearCustomerSessionCache,
  customerAccountQueryKey,
  customerReportsQueryKey,
  customerStationsQueryKey
} from './customer-session-cache'

type LoginPayload = {
  captcha?: string
  captchaKey?: string
  email: string
  password: string
}

type Options = {
  shouldLoadAccount?: boolean
}

const excludePaths = [
  '/customer/register',
  '/customer/register-successful',
  '/customer/login',
  '/customer/forgot-password',
  '/customer/reset-password'
]

export function useCustomerAccountAuthentication({
  shouldLoadAccount = true
}: Options = {}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { pathname } = useLocation()
  const shouldSkipAccountLookup = !shouldLoadAccount

  const authUserQuery = useQuery({
    queryKey: customerAccountQueryKey,
    enabled: !shouldSkipAccountLookup,
    refetchOnMount: false,
    queryFn: async () => {
      const response = await client.get<{ data: Customer }>('/app-api/me')
      return response.data.data
    },
    retry: false
  })

  useEffect(() => {
    if (shouldSkipAccountLookup) {
      return
    }

    if (authUserQuery.isError) {
      if (authUserQuery.data) {
        return
      }

      const isExcluded = excludePaths.some((path) => pathname.startsWith(path))

      if (!isExcluded) {
        void navigate('/customer/login')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authUserQuery.data,
    authUserQuery.isError,
    pathname,
    shouldSkipAccountLookup
  ])

  useEffect(() => {
    if (authUserQuery.data?.language) {
      void i18n.changeLanguage(authUserQuery.data.language)
    }
  }, [authUserQuery.data])

  const loginMutation = useMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: customerAccountQueryKey })
      await queryClient.cancelQueries({ queryKey: customerStationsQueryKey })
      await queryClient.cancelQueries({ queryKey: customerReportsQueryKey })
      clearCustomerSessionCache(queryClient)
    },
    mutationFn: async (payload: LoginPayload) => {
      const response = await client.post<{ data: Customer }>(
        '/app-api/web-login',
        {
          email: payload.email,
          password: payload.password,
          captcha: payload.captcha,
          captcha_key: payload.captchaKey
        }
      )

      return response.data.data
    },
    onSuccess: (customer) => {
      queryClient.setQueryData(customerAccountQueryKey, customer)
    },
    onError: restApiErrorHandler
  })

  const logoutMutation = useMutation({
    mutationFn: async () => client.post('/app-api/logout'),
    onSuccess: () => {
      clearCustomerSessionCache(queryClient)
      void navigate('/customer/login', { replace: true })
    }
  })

  const performLogout = useCallback(async () => {
    await logoutMutation.mutateAsync()
  }, [logoutMutation])

  const fetchAccount = useCallback(() => {
    void authUserQuery.refetch()
  }, [authUserQuery])

  const performLogin = useCallback(
    async (payload: LoginPayload) => {
      const customer = await loginMutation.mutateAsync(payload)
      void navigate('/customer', { replace: true })

      return customer
    },
    [loginMutation, navigate]
  )

  return {
    customer: authUserQuery.data,
    isValidating: shouldSkipAccountLookup ? false : authUserQuery.isLoading,
    isAuthenticated: authUserQuery.data != null,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    performLogin,
    performLogout,
    fetchAccount
  }
}
